import {
  Filter,
  ChevronDown,
  Star,
  Shield,
  Banknote,
  Search,
} from "lucide-react";

import {
  useAdvertisments,
  useCurrencies,
  useNoPaginationTokens,
} from "@/hooks";

import { useEffect, useMemo, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "@/store/hooks";

import { TradeStatus, TradeType } from "@/interfaces/trade";

import {
  setAmount,
  setPaymentMethodFilter,
  setSearchQuery,
  setSelectedAd,
  setSide,
  setMarketFilters,
  setCryptoCode,
  resetFilters,
} from "@/store/slices/p2pMarketSlice";

import type { Advertisement } from "@/interfaces/advertisement";

import { KycStatus } from "@/interfaces/kyc";

import { useNavigate } from "react-router-dom";

import { normalizeCurrency, removeTrailingToken } from "@/utils";

import Loader from "../loader/Loader";
import { ErrorState } from "../ErrorState";
import TradeModal from "./TradeModal";
import { MarketFilterButton } from "./MarketFilterButton";
import { MarketFilterSheet } from "./MarketFilterSheet";

import type { P2PMarketFilters } from "./filter/interface";

export const MarketView = () => {
  const [showTradeModal, setShowTradeModal] = useState(false);

  const [activeSheet, setActiveSheet] = useState<
    "crypto" | "amount" | "payment" | "search" | "filter" | null
  >(null);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const { data: tokens } = useNoPaginationTokens();

  const { data: currencies, isLoading: isLoadingCurrencies } = useCurrencies();

  const { theme } = useAppSelector((state) => state.ui);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    side,
    cryptoCode,
    fiatCurrencyId,
    amount,
    searchQuery,
    paymentMethodFilter,
    selectedAd,
    minAmount,
    maxAmount,
    paymentMethodIds,
    verifiedOnly,
    trustedOnly,
    minCompletionRate,
    maxReleaseTime,
    sortBy,
  } = useAppSelector((state) => state.p2pMarket);

  /*
   * Selected fiat currency.
   */
  const defaultFiat = currencies?.find(
    (currency) => currency.id === fiatCurrencyId,
  );

  /*
   * Selected crypto.
   */
  const crypto = tokens?.find((token) => token.code === cryptoCode)?.code ?? "";

  /*
   * These filters are still used by the UI.
   */
  const filters = useMemo<P2PMarketFilters>(
    () => ({
      cryptoCode,
      fiatCurrencyId,
      minAmount,
      maxAmount,
      paymentMethodIds,
      verifiedOnly,
      trustedOnly,
      minCompletionRate,
      maxReleaseTime,
      sortBy,
    }),
    [
      cryptoCode,
      fiatCurrencyId,
      minAmount,
      maxAmount,
      paymentMethodIds,
      verifiedOnly,
      trustedOnly,
      minCompletionRate,
      maxReleaseTime,
      sortBy,
    ],
  );

  /*
   * IMPORTANT:
   *
   * Send the actual filters to the backend.
   *
   * `amount` is a FIAT amount.
   *
   * Example:
   * amount = "50000"
   *
   * means ₦50,000 when the selected fiat is NGN.
   */
  const {
    data: ads,
    isLoading: isLoadingAds,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAdvertisments({
    side,
    crypto: cryptoCode,
    fiat: defaultFiat?.iso,
    amount,
    minAmount,
    maxAmount,
    search: searchQuery,
    paymentMethod: paymentMethodFilter,
  });

  const adsList = ads?.pages.flatMap((page) => page.data) ?? [];

  /*
   * Load more advertisements.
   */
  useEffect(() => {
    const element = loadMoreRef.current;

    if (!element) return;
    if (!hasNextPage) return;
    if (isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      {
        rootMargin: "400px",
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  /*
   * BUY view means the user wants to BUY crypto,
   * therefore SELL ads are displayed.
   *
   * SELL view means the user wants to SELL crypto,
   * therefore BUY ads are displayed.
   */
  const isBuyView = side === TradeType.BUY;

  /*
   * These are only used for the existing UI/local filters.
   *
   * Amount filtering itself is now handled by the backend
   * because it requires converting crypto limits to fiat
   * using each advertisement's price.
   */
  const filteredAds = adsList.filter((ad) => {
    const query = searchQuery.trim().toLowerCase();

    const matchesSearch =
      query === "" ||
      ad.user?.merchantName?.toLowerCase().includes(query) ||
      ad.paymentMethods?.some((method) =>
        method.bank?.name?.toLowerCase().includes(query),
      );

    const matchesType = isBuyView
      ? ad.type === TradeType.SELL
      : ad.type === TradeType.BUY;

    const matchesCrypto =
      !cryptoCode ||
      ad.asset?.currency?.toLowerCase() === cryptoCode.toLowerCase();

    const matchesFiat =
      !fiatCurrencyId || ad.fiatCurrency?.id === fiatCurrencyId;

    const matchesPayment =
      paymentMethodFilter === "" ||
      ad.paymentMethods?.some(
        (method) => method.bankId === paymentMethodFilter,
      );

    const matchesPaymentMethods =
      paymentMethodIds?.length === 0 ||
      paymentMethodIds?.some((paymentMethodId) =>
        ad.paymentMethods?.some((method) => method.bankId === paymentMethodId),
      );

    const matchesVerified =
      !verifiedOnly || ad.user?.kycStatus === KycStatus.APPROVED;

    const matchesTrusted =
      !trustedOnly || Number(ad.user?.completedTrades ?? 0) > 0;

    const completionRate = Number(ad.user?.reputationScore ?? 0);

    const matchesCompletionRate =
      minCompletionRate === null || completionRate >= minCompletionRate;

    const releaseTime = Number(
      ad.user?.averageReleaseTime ?? ad.user?.averageResponseTime ?? 0,
    );

    const matchesReleaseTime =
      maxReleaseTime === null || releaseTime <= maxReleaseTime;

    return (
      matchesSearch &&
      matchesType &&
      matchesCrypto &&
      matchesFiat &&
      matchesPayment &&
      matchesPaymentMethods &&
      matchesVerified &&
      matchesTrusted &&
      matchesCompletionRate &&
      matchesReleaseTime
    );
  });

  const hasAdvancedFilters =
    Boolean(cryptoCode) ||
    Boolean(fiatCurrencyId) ||
    Boolean(minAmount) ||
    Boolean(maxAmount) ||
    paymentMethodIds.length > 0 ||
    verifiedOnly ||
    trustedOnly ||
    minCompletionRate !== null ||
    maxReleaseTime !== null ||
    sortBy !== "BEST_MATCH";

  const handleStartTrade = (ad: Advertisement) => {
    dispatch(setSelectedAd(ad));

    dispatch(
      setSide(ad.type === TradeType.SELL ? TradeType.BUY : TradeType.SELL),
    );

    dispatch(setAmount(""));

    setShowTradeModal(true);
  };

  const getCompletedTradesCount = (side: TradeType) => {
    return side === TradeType.BUY
      ? selectedAd?.user?.buyerTrades?.filter(
          (ad) => ad.status === TradeStatus.RELEASED,
        ).length
      : selectedAd?.user?.sellerTrades?.filter(
          (ad) => ad.status === TradeStatus.RELEASED,
        ).length;
  };

  const count = getCompletedTradesCount(side);

  if (isLoadingAds) {
    return <Loader type="bounce" />;
  }

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  return (
    <>
      {/* Filter Bar */}
      <div
        className={`rounded-xl border p-4 px-1 mb-6 mt-8 ${
          theme === "dark"
            ? "bg-card border-border"
            : "bg-white border-gray-200"
        }`}
      >
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex w-full items-center justify-between gap-4">
            {/* Crypto */}
            <div className="w-fit">
              <MarketFilterButton
                label={
                  cryptoCode ? normalizeCurrency(cryptoCode) : "Cryptocurrency"
                }
                icon={ChevronDown}
                theme={theme}
                active={Boolean(cryptoCode)}
                onClick={() => setActiveSheet("crypto")}
              />
            </div>

            {/* Buy / Sell */}
            <div
              className={`flex h-11 rounded-xl p-1 items-center ${
                theme === "dark" ? "bg-muted/60" : "bg-gray-100"
              }`}
            >
              <button
                type="button"
                onClick={() => dispatch(setSide(TradeType.BUY))}
                className={`flex-1 h-full rounded-lg px-5 text-sm font-semibold leading-none transition-all duration-200 ${
                  side === TradeType.BUY
                    ? "bg-[#0ECB81] text-white shadow-sm"
                    : theme === "dark"
                      ? "text-muted-foreground hover:text-foreground"
                      : "text-gray-500 hover:text-gray-900"
                }`}
              >
                Buy
              </button>

              <button
                type="button"
                onClick={() => dispatch(setSide(TradeType.SELL))}
                className={`flex-1 h-full rounded-lg px-5 text-sm font-semibold leading-none transition-all duration-200 ${
                  side === TradeType.SELL
                    ? "bg-[#F6465D] text-white shadow-sm"
                    : theme === "dark"
                      ? "text-muted-foreground hover:text-foreground"
                      : "text-gray-500 hover:text-gray-900"
                }`}
              >
                Sell
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 w-full">
            {/* Amount */}
            <MarketFilterButton
              label={
                minAmount || maxAmount
                  ? `${minAmount ? Number(minAmount).toLocaleString() : "0"} - ${
                      maxAmount ? Number(maxAmount).toLocaleString() : "∞"
                    } ${defaultFiat?.iso ?? ""}`
                  : "Amount"
              }
              icon={ChevronDown}
              theme={theme}
              active={Boolean(minAmount || maxAmount)}
              onClick={() => setActiveSheet("amount")}
            />

            {/* Payment */}
            <MarketFilterButton
              label={
                paymentMethodFilter
                  ? (defaultFiat?.banks?.find(
                      (bank) => bank.id === paymentMethodFilter,
                    )?.name ?? "Payment")
                  : "Payment"
              }
              icon={ChevronDown}
              theme={theme}
              active={Boolean(paymentMethodFilter)}
              onClick={() => setActiveSheet("payment")}
            />

            {/* Search */}
            <MarketFilterButton
              label={searchQuery || "Search"}
              icon={Search}
              theme={theme}
              active={Boolean(searchQuery)}
              onClick={() => setActiveSheet("search")}
            />

            {/* Advanced Filter */}
            <MarketFilterButton
              label="Filter"
              icon={Filter}
              theme={theme}
              active={hasAdvancedFilters}
              onClick={() => setActiveSheet("filter")}
            />
          </div>
        </div>
      </div>

      <MarketFilterSheet
        open={activeSheet !== null}
        onOpenChange={(open) => {
          if (!open) {
            setActiveSheet(null);
          }
        }}
        type={activeSheet ?? "filter"}
        theme={theme}
        amount={amount}
        paymentMethodFilter={paymentMethodFilter}
        searchQuery={searchQuery}
        cryptoCode={cryptoCode}
        fiatCurrencyId={fiatCurrencyId}
        tokens={tokens}
        currencies={currencies}
        filters={filters}
        onApplyAmount={(min, max) => {
          dispatch(
            setMarketFilters({
              minAmount: min.trim(),
              maxAmount: max.trim(),
            }),
          );
        }}
        onApplyPayment={(value) => {
          dispatch(setPaymentMethodFilter(value));
        }}
        onApplySearch={(value) => {
          dispatch(setSearchQuery(value));
        }}
        onApplyCrypto={(value) => {
          dispatch(setCryptoCode(value));
        }}
        onApplyFilters={(filters) => {
          dispatch(setMarketFilters(filters));
        }}
        onClear={() => {
          dispatch(resetFilters());
        }}
      />

      {/* Results Count */}
      <div className="flex items-center justify-between mb-4">
        <p
          className={`text-sm ${
            theme === "dark" ? "text-muted-foreground" : "text-gray-500"
          }`}
        >
          Showing {filteredAds.length} loaded offers
        </p>
      </div>

      {/* Offers List */}
      <div className="space-y-4">
        {filteredAds.map((ad) => {
          return (
            <div
              key={ad.id}
              className={`rounded-lg border transition-all duration-200 hover:border-primary/40 hover:shadow-md ${
                theme === "dark"
                  ? "bg-card border-border"
                  : "bg-white border-gray-200"
              }`}
            >
              <div className="grid grid-cols-12 items-center gap-3 px-4 py-3">
                {/* Merchant */}
                <div
                  onClick={() => navigate(`/p2p/merchant/${ad.userId}`)}
                  className="col-span-12 lg:col-span-4 flex items-center gap-3 cursor-pointer min-w-0"
                >
                  <div className="w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-[#F0B90B] to-[#E07B39] flex items-center justify-center font-bold text-sm text-black">
                    {ad?.user?.avatar || ""}
                  </div>

                  <div className="min-w-0">
                    <div className="flex items-center gap-1">
                      <span
                        className={`truncate font-semibold ${
                          theme === "dark" ? "text-foreground" : "text-gray-900"
                        }`}
                      >
                        {ad?.user?.merchantName}
                      </span>

                      {ad?.user?.kycStatus === KycStatus.APPROVED && (
                        <Shield className="w-4 h-4 text-primary shrink-0" />
                      )}
                    </div>

                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs">
                      <span className="flex items-center gap-1 text-primary">
                        <Star className="w-3 h-3 fill-[#F0B90B]" />

                        {ad?.user?.reputationScore}
                      </span>

                      <span
                        className={
                          theme === "dark"
                            ? "text-muted-foreground"
                            : "text-gray-500"
                        }
                      >
                        {count} {Number(count) > 1 ? "Trades" : "Trade"}
                      </span>

                      <span
                        className={
                          theme === "dark"
                            ? "text-muted-foreground"
                            : "text-gray-500"
                        }
                      >
                        {ad?.user?.completedTrades}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Price */}
                <div className="col-span-4 sm:col-span-3 lg:col-span-2">
                  <p
                    className={`text-[11px] ${
                      theme === "dark"
                        ? "text-muted-foreground"
                        : "text-gray-500"
                    }`}
                  >
                    Price
                  </p>

                  <div
                    className={`font-mono text-lg font-bold whitespace-nowrap flex flex-col sm:flex-row gap-1 ${
                      theme === "dark" ? "text-foreground" : "text-gray-900"
                    }`}
                  >
                    <span>{Number(ad?.price).toFixed(2)}</span>

                    <span>{defaultFiat?.iso}</span>
                  </div>
                </div>

                {/* Available */}
                <div className="col-span-4 sm:col-span-3 lg:col-span-2">
                  <p
                    className={`text-[11px] ${
                      theme === "dark"
                        ? "text-muted-foreground"
                        : "text-gray-500"
                    }`}
                  >
                    Available
                  </p>

                  <p
                    className={`font-mono text-sm font-medium whitespace-nowrap ${
                      theme === "dark" ? "text-foreground" : "text-gray-900"
                    }`}
                  >
                    {ad?.available?.toFixed(2) ?? 0} {normalizeCurrency(crypto)}
                  </p>
                </div>

                {/* Limits */}
                <div className="col-span-4 sm:col-span-3 lg:col-span-2 min-w-0">
                  <p
                    className={`text-[11px] ${
                      theme === "dark"
                        ? "text-muted-foreground"
                        : "text-gray-500"
                    }`}
                  >
                    Limits
                  </p>

                  <p
                    className={`font-mono text-sm break-words leading-5 ${
                      theme === "dark" ? "text-foreground" : "text-gray-900"
                    }`}
                  >
                    {(Number(ad.minAmount) * Number(ad.price)).toFixed(2)}

                    <span className="mx-1">-</span>

                    {(Number(ad.maxAmount) * Number(ad.price)).toFixed(2)}
                  </p>

                  <p
                    className={`text-[11px] ${
                      theme === "dark"
                        ? "text-muted-foreground"
                        : "text-gray-500"
                    }`}
                  >
                    {removeTrailingToken(defaultFiat?.iso ?? "")}
                  </p>
                </div>

                {/* Payment */}
                <div className="col-span-8 sm:col-span-9 lg:col-span-1">
                  <p
                    className={`text-[11px] mb-1 ${
                      theme === "dark"
                        ? "text-muted-foreground"
                        : "text-gray-500"
                    }`}
                  >
                    Payment
                  </p>

                  <div className="flex flex-wrap gap-1">
                    {ad?.paymentMethods?.slice(0, 2).map((method) => (
                      <span
                        key={method.id}
                        className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] ${
                          theme === "dark"
                            ? "bg-muted text-muted-foreground"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        <Banknote className="w-3 h-3" />

                        {method.bank.name}
                      </span>
                    ))}

                    {ad?.paymentMethods && ad.paymentMethods.length > 2 && (
                      <span className="text-[11px] text-primary self-center">
                        +{ad.paymentMethods.length - 2}
                      </span>
                    )}
                  </div>
                </div>

                {/* Action */}
                <div className="col-span-4 sm:col-span-3 lg:col-span-1 flex justify-end items-center">
                  <button
                    onClick={() => handleStartTrade(ad)}
                    className={`h-9 whitespace-nowrap rounded-md px-4 text-sm font-semibold transition-all hover:scale-[1.02] ${
                      side === TradeType.BUY
                        ? "bg-[#0ECB81] text-white hover:brightness-110"
                        : "bg-[#F6465D] text-white hover:brightness-110"
                    }`}
                  >
                    {side === TradeType.BUY ? "Buy" : "Sell"}{" "}
                    {normalizeCurrency(crypto)}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Infinite Scroll */}
      <div ref={loadMoreRef} className="h-10 flex items-center justify-center">
        {isFetchingNextPage && <Loader type="bounce" />}

        {!hasNextPage && filteredAds.length > 0 && (
          <p
            className={`text-sm ${
              theme === "dark" ? "text-muted-foreground" : "text-gray-500"
            }`}
          >
            No more offers
          </p>
        )}
      </div>

      {/* Safety Tips */}
      <div
        className={`rounded-xl border p-6 mt-12 ${
          theme === "dark"
            ? "bg-card border-border"
            : "bg-white border-gray-200"
        }`}
      >
        <h3
          className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
            theme === "dark" ? "text-foreground" : "text-gray-900"
          }`}
        >
          <Shield className="w-5 h-5 text-primary" />
          Safety Tips
        </h3>

        <div className="grid md:grid-cols-3 gap-4">
          {[
            {
              title: "Verify Payment",
              desc: "Always confirm payment received before releasing crypto",
            },
            {
              title: "Use Escrow",
              desc: "Never trade outside the platform",
            },
            {
              title: "Check Ratings",
              desc: "Trade with verified users only",
            },
          ].map((tip, i) => (
            <div
              key={i}
              className={`p-4 rounded-lg ${
                theme === "dark" ? "bg-muted" : "bg-gray-100"
              }`}
            >
              <h4
                className={`font-medium mb-1 ${
                  theme === "dark" ? "text-foreground" : "text-gray-900"
                }`}
              >
                {tip.title}
              </h4>

              <p
                className={`text-sm ${
                  theme === "dark" ? "text-muted-foreground" : "text-gray-500"
                }`}
              >
                {tip.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Trade Modal */}
        {showTradeModal && selectedAd && (
          <TradeModal
            showTradeModal={showTradeModal}
            setShowTradeModal={setShowTradeModal}
            side={side}
            crypto={crypto}
            selectedAd={selectedAd}
            amount={amount}
          />
        )}
      </div>
    </>
  );
};
